/**
 * Phase 32 — the two daily summaries of a channel, each one self-explaining
 * row: what it is, when it comes, and an example of the message exactly as
 * the dispatcher writes it (user, 13/10/2026: "the digest I don't
 * understand — be clear").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Sunrise, Sunset } from 'lucide-react';

import { Switch } from '@/components/ui';

import { clockText } from '../../../utils/clock/dk-label';
import { hhmmToMinutes } from '../../../utils/format';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { TimeField } from '../times/TimeField';

const EXAMPLE = 'rounded-md bg-muted/40 px-2 py-1 font-mono text-[11px] leading-snug text-muted-foreground';

const Row: FC<{ on: boolean; disabled: boolean; onToggle: (on: boolean) => void; label: string; Icon: typeof Sunrise; title: ReactNode; what: string; example: string }> = ({
  on,
  disabled,
  onToggle,
  label,
  Icon,
  title,
  what,
  example,
}) => (
  <div className="flex items-start gap-2.5 text-xs">
    <Switch checked={on} disabled={disabled} onCheckedChange={onToggle} aria-label={label} className="mt-0.5" />
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
    <div className="flex min-w-0 flex-col gap-1">
      <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
        <b className="font-medium text-foreground">{label}</b>
        {title}
      </span>
      <span className="text-muted-foreground">{what}</span>
      <span className={EXAMPLE}>{example}</span>
    </div>
  </div>
);

/** A summary's time as text, Danish first (QA 18/10/2026: "at 08:05" had no flag). */
const ClockAt: FC<{ hhmm: string; tz: string | null }> = ({ hhmm, tz }) => {
  const clock = useCountryClock();
  const min = hhmmToMinutes(hhmm);
  return <>{min === null ? hhmm : clockText(min, { ...clock, tz: tz ?? clock.tz })}</>;
};

export const DigestRows: FC<{
  countryName: string;
  digestAt: string | null;
  dayEndDigest: boolean;
  /** HH:MM used when the morning summary is switched on. */
  defaultDigestAt: string;
  recipientTimezone: string | null;
  windowEnd: string;
  disabled?: boolean;
  onChange: (patch: { digestAt?: string | null; dayEndDigest?: boolean }) => void;
}> = ({ countryName, digestAt, dayEndDigest, defaultDigestAt, recipientTimezone, windowEnd, disabled = false, onChange }) => (
  <div className="flex flex-col gap-3">
    <Row
      on={digestAt !== null}
      disabled={disabled}
      onToggle={(on) => onChange({ digestAt: on ? defaultDigestAt : null })}
      label="Morning summary"
      Icon={Sunrise}
      title={
        <>
          <span className="text-muted-foreground">· one message</span>
          {digestAt !== null ? (
            <span className="duration-300 animate-in fade-in-0 slide-in-from-left-1 motion-reduce:animate-none">
              <TimeField inline label="at" value={digestAt} onChange={(v) => onChange({ digestAt: v })} recipientTimezone={recipientTimezone} />
            </span>
          ) : (
            <span className="text-muted-foreground">
              at <ClockAt hhmm={defaultDigestAt} tz={recipientTimezone} />
            </span>
          )}
        </>
      }
      what="once the day is laid out: how many drops and SMS are planned, and any warning"
      example={`📣 ${countryName} — today laid out: 42 drops · 18,400 SMS · 1 warning: • a drop falls outside the sending window`}
    />
    <Row
      on={dayEndDigest}
      disabled={disabled}
      onToggle={(on) => onChange({ dayEndDigest: on })}
      label="Day-end summary"
      Icon={Sunset}
      title={
        <span className="text-muted-foreground">
          · one message at <ClockAt hhmm={windowEnd} tz={recipientTimezone} />, when sending closes
        </span>
      }
      what="what went out today and what the judge decided"
      example={`📣 ${countryName} — today is over: 40 of 42 drops sent · 17,900 SMS • the judge: 3 PASS · 1 FAIL · 1 inconclusive — 1 list burned`}
    />
  </div>
);
